import React, { useState, useCallback } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { getCroppedImg } from '../utils/imageUtils';
import { handleSliderPointerDown } from '../utils/helpers';

export const PfpCropperModal = ({ image, onClose, onCropComplete }: { image: string, onClose: () => void, onCropComplete: (blob: Blob) => void }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const onCropChange = (crop: { x: number, y: number }) => setCrop(crop);
    const onZoomChange = (zoom: number) => setZoom(zoom);
    const onCropCompleteInternal = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleDone = async () => {
        if (!croppedAreaPixels) return;
        setIsProcessing(true);
        try {
            const blob = await getCroppedImg(image, croppedAreaPixels);
            if (blob) {
                await onCropComplete(blob);
            }
        } catch (e) {
            console.error("Cropping failed", e);
            alert("Failed to crop image.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/90 z-[200] flex flex-col items-center justify-center p-4">
            <div className="relative w-full max-w-lg aspect-square bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
                <Cropper
                    image={image}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={onCropChange}
                    onCropComplete={onCropCompleteInternal}
                    onZoomChange={onZoomChange}
                    cropShape="round"
                    showGrid={false}
                />
            </div>
            <div className="w-full max-w-lg mt-6 space-y-4">
                <div className="flex items-center gap-4">
                    <span className="text-white text-sm font-medium">Zoom</span>
                    <input
                        type="range"
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        aria-labelledby="Zoom"
                        onPointerDown={(e) => handleSliderPointerDown(e, zoom, 1, 3)}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        className="flex-grow accent-blue-500"
                    />
                </div>
                <div className="flex justify-between gap-4 pt-2">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 px-6 rounded-full bg-gray-800 text-white font-bold hover:bg-gray-700 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDone}
                        disabled={isProcessing}
                        className="flex-1 py-3 px-6 rounded-full bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        {isProcessing ? 'Processing...' : 'Done'}
                    </button>
                </div>
            </div>
        </div>
    );
};
